import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Calendar, User, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { getBlogs } from "../services/services";

const IMAGE = "https://miniapptech.in/assets/img/blog";
const VIDEO = "https://miniapptech.in/assets/img/blog_video";
const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    const res = await getBlogs();

    if (res.status) {
      setPosts(res.data.slice(0, 3));
    }
    setLoading(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.5 },
    },
  };

  const getEmbedUrl = (url) => {
    if (!url) return "";

    if (url.includes("youtu.be")) {
      const id = url.split("/").pop().split("?")[0];
      return `https://www.youtube.com/embed/${id}`;
    }

    if (url.includes("youtube.com")) {
      const id = new URL(url).searchParams.get("v");
      return `https://www.youtube.com/embed/${id}`;
    }

    return url;
  };

  const isYouTube = (url) =>
    url?.includes("youtube.com") || url?.includes("youtu.be");

  const renderMedia = (post) => {
    const hasVideo = post?.video?.trim();
    const hasImages = Array.isArray(post?.image) && post.image.length > 0;

    if (hasVideo) {
      return isYouTube(post.video) ? (
        <iframe
          src={getEmbedUrl(post.video)}
          className="w-full h-full"
          allowFullScreen
          title="video"
        />
      ) : (
        <video className="w-full h-full object-cover" muted autoPlay loop>
          <source src={`${VIDEO}/${post.video}`} type="video/mp4" />
        </video>
      );
    }

    if (hasImages) {
      return (
        <img
          src={`${IMAGE}/${post.image[0]}`}
          alt={post.title || "blog"}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
      );
    }

    return (
      <div className="w-full h-full flex items-center justify-center text-gray-400">
        No Media
      </div>
    );
  };

  return (
    <section id="blog" className="py-20 bg-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-dark mb-4">
            Latest <span className="text-primary">Insights</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Stay updated with our latest thoughts on technology, design, and
            business strategy.
          </p>
        </motion.div>
        {loading ? (
          <div className="text-center py-10 text-gray-500">
            Loading blogs...
          </div>
        ) : (
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {posts.map((post, index) => (
              <motion.div
                key={post.blog_detail_id}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                className="bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-soft-lg transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden bg-black flex items-center justify-center">
                  {renderMedia(post)}

                  <div className="absolute top-4 left-4 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                    {post.category || "General"}
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-4 gap-4">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} /> {post.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <User size={14} /> {post.author}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-dark mb-3 group-hover:text-primary transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>

                  <Link
                    to={`/blog/${post.blog_detail_id}`}
                    className="inline-flex items-center gap-2 text-primary font-bold hover:gap-3 transition-all"
                  >
                    Read More <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
        <div className="mt-12 text-center">
          <Link
            to="/blog"
            className="inline-block bg-primary hover:bg-secondary text-white px-8 py-3 rounded-xl font-bold transition-all hover:shadow-lg"
          >
            View All Insights
          </Link>
        </div>
      </div>
    </section>
  );
};

export default Blog;
